import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import type { ChatItem } from '../types';
import { getChatCollectionName } from './useProfile';

const TYPING_IDLE_MS = 4000;
// A typing doc older than this is treated as stale (e.g. the app was killed
// mid-type without ever clearing it) rather than "still typing forever".
const TYPING_STALE_MS = 8000;

function typingRef(chatId: string, kind: ChatItem['kind'], uid: string) {
  return doc(firestore, getChatCollectionName(kind), chatId, 'typing', uid);
}

/** Call `notifyTyping()` on every keystroke; it self-throttles and auto-clears. */
export function useTypingIndicator(chatId: string | null, kind: ChatItem['kind'], uid: string | null) {
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMarkedTypingRef = useRef(false);

  const clearTyping = useCallback(() => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
    if (!chatId || !uid || !isMarkedTypingRef.current) return;
    isMarkedTypingRef.current = false;
    deleteDoc(typingRef(chatId, kind, uid)).catch(() => {});
  }, [chatId, kind, uid]);

  const notifyTyping = useCallback(() => {
    if (!chatId || !uid) return;
    if (!isMarkedTypingRef.current) {
      isMarkedTypingRef.current = true;
      setDoc(typingRef(chatId, kind, uid), { isTyping: true, updatedAt: serverTimestamp() }).catch(() => {});
    }
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(clearTyping, TYPING_IDLE_MS);
  }, [chatId, clearTyping, kind, uid]);

  useEffect(() => clearTyping, [clearTyping]);

  return { notifyTyping, clearTyping };
}

/** Read side — is anyone else in this chat currently typing? For 1:1 chats this is just the other participant. */
export function useOtherUsersTyping(chatId: string | null, kind: ChatItem['kind'], currentUserId: string | null) {
  const [typingUserIds, setTypingUserIds] = useState<string[]>([]);

  useEffect(() => {
    if (!chatId) {
      setTypingUserIds([]);
      return undefined;
    }
    const unsub = onSnapshot(
      collection(firestore, getChatCollectionName(kind), chatId, 'typing'),
      snapshot => {
        const now = Date.now();
        const ids = snapshot.docs
          .filter(d => {
            if (d.id === currentUserId) return false;
            const data = d.data();
            if (data.isTyping !== true) return false;
            const updatedAtMs = typeof data.updatedAt?.toMillis === 'function' ? data.updatedAt.toMillis() : now;
            return now - updatedAtMs < TYPING_STALE_MS;
          })
          .map(d => d.id);
        setTypingUserIds(ids);
      },
      error => {
        console.warn('[Tonyte][RN][Typing] subscription failed', { chatId, kind, error });
      },
    );
    return unsub;
  }, [chatId, currentUserId, kind]);

  return typingUserIds;
}
